import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { hmdaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import ActiveBankNotice from '../components/ActiveBankNotice';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { PageHeader } from '../components/ui';

// All suppressible quality & macro edit codes with short descriptions
const QUALITY_EDITS: { code: string; label: string }[] = [
  { code: 'Q600', label: 'Application date >2 years before action date' },
  { code: 'Q601', label: 'Action taken date is in the future' },
  { code: 'Q602', label: 'Loan amount exceeds conforming limit' },
  { code: 'Q603', label: 'Income unusually high for loan amount' },
  { code: 'Q604', label: 'Applicant age <18 or >100' },
  { code: 'Q605', label: 'Co-applicant age <18 or >100' },
  { code: 'Q606', label: 'Credit score outside expected range' },
  { code: 'Q607', label: 'Co-applicant credit score outside range' },
  { code: 'Q608', label: 'Rate spread unusually high' },
  { code: 'Q609', label: 'Total loan costs seem high' },
  { code: 'Q610', label: 'Origination charges unusually high' },
  { code: 'Q611', label: 'Discount points seem high' },
  { code: 'Q612', label: 'Lender credits unusually high' },
  { code: 'Q613', label: 'Interest rate outside expected range' },
  { code: 'Q614', label: 'Prepayment penalty term seems long' },
  { code: 'Q615', label: 'DTI ratio seems very high' },
  { code: 'Q616', label: 'CLTV ratio seems very high' },
  { code: 'Q617', label: 'Loan term unusually long' },
  { code: 'Q618', label: 'Property value outside expected range' },
  { code: 'Q619', label: 'Multifamily units count seems high' },
  { code: 'Q620', label: 'Purchased loan with origination data' },
  { code: 'Q621', label: 'Denied with loan costs reported' },
  { code: 'Q622', label: 'Withdrawn with pricing data' },
  { code: 'Q623', label: 'Incomplete with pricing data' },
  { code: 'Q624', label: 'Preapproval denied with pricing' },
  { code: 'Q625', label: 'Reverse mortgage with unusual features' },
  { code: 'Q626', label: 'Open-end line with balloon payment' },
  { code: 'Q627', label: 'Business loan with personal demographics' },
  { code: 'Q628', label: 'Exempt fields have data' },
  { code: 'Q634_macro', label: 'Macro: Conventional loan share unusual' },
  { code: 'Q635_macro', label: 'Macro: Home purchase share unusual' },
  { code: 'Q636_macro', label: 'Macro: Refinance share unusual' },
  { code: 'Q637_macro', label: 'Macro: Cash-out refi share unusual' },
  { code: 'Q638_macro', label: 'Macro: Home improvement share unusual' },
  { code: 'Q639_macro', label: 'Macro: Multifamily share unusual' },
  { code: 'Q640_macro', label: 'Macro: Owner-occupied share low' },
  { code: 'Q641_macro', label: 'Macro: Site-built share unusual' },
  { code: 'Q642_macro', label: 'Macro: Average loan amount unusual' },
  { code: 'Q643_macro', label: 'Macro: Denial rate exceeds 70%' },
  { code: 'Q644_macro', label: 'Macro: Originated share unusual' },
  { code: 'Q645_macro', label: 'Macro: Loan count differs >20% from TS' },
];

export default function HmdaUploadPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [showEditSettings, setShowEditSettings] = useState(false);

  // Bind to the ACTIVE team rather than a per-feature picker (no wrong-bank footgun).
  const { teamId: activeTeamId } = useTeamId();

  // Suppressed edit codes for team
  const { data: suppressedData } = useQuery({
    queryKey: ['hmda-suppressed-edits', activeTeamId],
    queryFn: async () => {
      if (!activeTeamId) return { suppressedEdits: [] };
      const response = await hmdaApi.getSuppressedEdits(activeTeamId);
      return response.data;
    },
    enabled: !!activeTeamId,
  });
  const suppressedEdits = new Set<string>(suppressedData?.suppressedEdits || []);

  const suppressMutation = useMutation({
    mutationFn: (editCodes: string[]) => hmdaApi.setSuppressedEdits(activeTeamId ?? '', editCodes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmda-suppressed-edits', activeTeamId] });
      toast.success('Edit check settings saved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save settings');
    },
  });

  const toggleEditCode = (code: string) => {
    const next = new Set(suppressedEdits);
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    suppressMutation.mutate(Array.from(next));
  };

  // Recent validations
  const { data: recentData, refetch: refetchRecent } = useQuery({
    queryKey: ['hmda-validations-recent'],
    queryFn: async () => {
      const response = await hmdaApi.getValidations({ limit: 5 });
      return response.data;
    },
  });

  // Single file validation
  const validateMutation = useMutation({
    mutationFn: async () => {
      if (files.length === 0) throw new Error('No file selected');
      return hmdaApi.validate(files[0], activeTeamId || undefined);
    },
    onSuccess: (response) => {
      const { validationId, summary } = response.data;
      toast.success(
        `Validation complete: ${summary.totalIssues} issue${summary.totalIssues !== 1 ? 's' : ''} found`,
      );
      setFiles([]);
      refetchRecent();
      navigate(`/hmda/validations/${validationId}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Validation failed');
    },
  });

  // Batch validation
  const batchMutation = useMutation({
    mutationFn: async () => {
      return hmdaApi.validateBatch(files, activeTeamId || undefined);
    },
    onSuccess: (response) => {
      const { count, results } = response.data;
      const totalIssues = results.reduce((sum: number, r: any) => sum + r.summary.totalIssues, 0);
      toast.success(`${count} files validated: ${totalIssues} total issue${totalIssues !== 1 ? 's' : ''}`);
      setFiles([]);
      refetchRecent();
      // Navigate to the first result
      if (results.length === 1) {
        navigate(`/hmda/validations/${results[0].validationId}`);
      } else {
        navigate('/hmda/validations');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Batch validation failed');
    },
  });

  const demoMutation = useMutation({
    mutationFn: async () => {
      return hmdaApi.runDemo(activeTeamId || undefined);
    },
    onSuccess: (response) => {
      const { validationId, summary } = response.data;
      toast.success(
        `Demo validation complete: ${summary.totalIssues} issue${summary.totalIssues !== 1 ? 's' : ''} found`,
      );
      refetchRecent();
      navigate(`/hmda/validations/${validationId}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Demo validation failed');
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFiles(prev => {
        const existing = new Set(prev.map(f => f.name));
        const newFiles = acceptedFiles.filter(f => !existing.has(f.name));
        return [...prev, ...newFiles].slice(0, 10); // Max 10 files
      });
    }
  }, []);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/octet-stream': ['.dat'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize: 100 * 1024 * 1024,
    multiple: true,
    maxFiles: 10,
  });

  const handleValidate = () => {
    if (files.length === 0) {
      toast.error('Please select a LAR file');
      return;
    }
    if (files.length === 1) {
      validateMutation.mutate();
    } else {
      batchMutation.mutate();
    }
  };

  const isProcessing = validateMutation.isPending || batchMutation.isPending;
  const validations = recentData?.validations || [];

  return (
    <div>
      <PageHeader
        title="LAR Validation"
        description="Upload your Loan Application Register file for CFPB edit check validation before submission"
      />

      {/* Active bank — HMDA validates under the active tenant (switch via the sidebar). */}
      <ActiveBankNotice className="mb-6" />

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
          isDragActive
            ? 'border-ocean-500 bg-ocean-50 shadow-lg'
            : 'border-gray-300 hover:border-ocean-400 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <TableCellsIcon className={`mx-auto h-12 w-12 transition-colors ${isDragActive ? 'text-ocean-500' : 'text-gray-400'}`} />
        <p className="mt-2 text-sm font-medium text-navy-900">
          {isDragActive ? 'Drop LAR files here' : 'Drag & drop LAR files here, or click to select'}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Pipe-delimited .txt, .csv, .dat, or Excel .xlsx files (max 100MB each, up to 10 files)
        </p>
      </div>

      {/* Selected files */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6"
          >
            <div className="space-y-2">
              {files.map((file, index) => (
                <div key={file.name} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="h-5 w-5 text-ocean-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-4">
              <button
                onClick={handleValidate}
                disabled={isProcessing}
                className="flex-1 bg-ocean-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-600 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              >
                {isProcessing ? (
                  <span className="inline-flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Validating{files.length > 1 ? ` ${files.length} files` : ''}...
                  </span>
                ) : (
                  files.length > 1
                    ? `Validate ${files.length} Files`
                    : 'Run CFPB Edit Checks'
                )}
              </button>
              <button
                onClick={() => setFiles([])}
                className="px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Try Demo — visible when no file is selected */}
      {files.length === 0 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 mb-2">
            {validations.length === 0
              ? "New here? Try the demo to see how validation works."
              : "Want to see the full feature set?"}
          </p>
          <button
            onClick={() => demoMutation.mutate()}
            disabled={demoMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-ocean-300 bg-white px-5 py-2.5 text-sm font-medium text-ocean-700 hover:bg-ocean-50 hover:border-ocean-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {demoMutation.isPending ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Running demo...
              </>
            ) : (
              <>
                <TableCellsIcon className="h-4 w-4" />
                Try with Sample LAR File
              </>
            )}
          </button>
        </div>
      )}

      {/* Info section */}
      <div className="mt-12 bg-ocean-50 border border-ocean-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-navy-900 mb-2">
          What happens after upload?
        </h3>
        <ul className="text-sm text-navy-700 space-y-1">
          <li>&#8226; File is parsed as a pipe-delimited HMDA LAR (or converted from Excel)</li>
          <li>&#8226; Transmittal Sheet (Row 1) is validated for format and institution info</li>
          <li>&#8226; All 110 fields per loan are checked against CFPB edit rules</li>
          <li>&#8226; Syntactical and validity errors block submission</li>
          <li>&#8226; Quality and macro warnings can be verified and submitted</li>
          <li>&#8226; AI generates plain-English explanations and suggested fixes</li>
        </ul>
      </div>

      {/* Edit Check Customization */}
      {activeTeamId && (
        <div className="mt-8">
          <button
            onClick={() => setShowEditSettings(!showEditSettings)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Edit Check Settings
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showEditSettings ? 'rotate-180' : ''}`} />
            {suppressedEdits.size > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                {suppressedEdits.size} suppressed
              </span>
            )}
          </button>

          {showEditSettings && (
            <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-3">
                Suppress quality and macro warning edit codes for this team.
                Syntactical and validity errors cannot be suppressed (CFPB requirement).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto">
                {QUALITY_EDITS.map(({ code, label }) => (
                  <label
                    key={code}
                    className={`flex items-start gap-2 px-2.5 py-1.5 rounded cursor-pointer transition-colors text-xs ${
                      suppressedEdits.has(code)
                        ? 'bg-amber-50 text-amber-800'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={suppressedEdits.has(code)}
                      onChange={() => toggleEditCode(code)}
                      className="mt-0.5 rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
                    />
                    <span>
                      <span className="font-mono font-medium">{code}</span>{' '}
                      <span className="text-gray-500">{label}</span>
                    </span>
                  </label>
                ))}
              </div>
              {suppressedEdits.size > 0 && (
                <button
                  onClick={() => suppressMutation.mutate([])}
                  className="mt-3 text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Clear all suppressions
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent validations */}
      {validations.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-navy-900">Recent Validations</h2>
            <button
              onClick={() => navigate('/hmda/validations')}
              className="text-sm text-ocean-600 hover:text-ocean-700 font-medium transition-colors"
            >
              View All &rarr;
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {validations.map((v: any) => {
                const hasErrors = v.syntacticalErrors > 0 || v.validityErrors > 0;
                const hasWarnings = v.qualityWarnings > 0 || v.macroWarnings > 0;
                const isClean = v.totalIssues === 0;
                return (
                  <li
                    key={v.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/hmda/validations/${v.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{v.fileName}</p>
                          <p className="text-xs text-gray-500">
                            {v.institutionName} &middot; {v.totalLoans?.toLocaleString()} loans &middot;{' '}
                            {formatDistanceToNow(new Date(v.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isClean && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            Clean
                          </span>
                        )}
                        {hasErrors && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                            {v.syntacticalErrors + v.validityErrors} error{v.syntacticalErrors + v.validityErrors !== 1 ? 's' : ''}
                          </span>
                        )}
                        {hasWarnings && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            {v.qualityWarnings + v.macroWarnings} warning{v.qualityWarnings + v.macroWarnings !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
